/**
 * "Testar como conversa" (#1012): o mapa aberto, percorrido como uma conversa
 * de WhatsApp. A automação fala, o menu aparece com botões, e quem testa toca
 * neles como o cliente. Nada é enviado nem gravado, e não precisa salvar antes:
 * a conversa usa o mapa como está na tela. A lógica mora em
 * `lib/conversa-de-teste.ts`.
 */

import { useState, type FormEvent, type ReactNode } from 'react';
import type { BlocosDoEditor } from '@cliqcrm/contracts';
import { Botao, Campo, CampoSelecao, Dialogo } from '@/shared/ui';
import type { Configuracao } from '../lib/edicao';
import { resumoDoComeco } from '../lib/comeco';
import { SEM_RESPOSTA } from '../lib/menu';
import {
  comecarConversa,
  escolherOpcao,
  escreverComoCliente,
  seguirPor,
  type EstadoDaConversa,
} from '../lib/conversa-de-teste';
import './conversa-de-teste.css';

export function ConversaDeTeste({
  config,
  blocos,
  aoFechar,
}: {
  config: Configuracao;
  blocos: BlocosDoEditor | undefined;
  aoFechar: () => void;
}): ReactNode {
  // Por qual começo testar (#1183): 0 é o gatilho; 1 em diante, os outros.
  const [comeco, definirComeco] = useState(0);
  const [estado, definirEstado] = useState<EstadoDaConversa>(() =>
    comecarConversa(config, blocos, 'Oi', 0),
  );
  const [texto, definirTexto] = useState('');
  const recomecar = (por: number): void => {
    definirComeco(por);
    definirEstado(comecarConversa(config, blocos, 'Oi', por));
    definirTexto('');
  };
  const pergunta = estado.pergunta;
  // As opções que ainda dá para tocar: as do último menu, enquanto ele espera.
  const indiceDoMenuAberto =
    pergunta?.tipo === 'menu' ? estado.falas.map((f) => Boolean(f.opcoes)).lastIndexOf(true) : -1;

  const escrever = (e: FormEvent): void => {
    e.preventDefault();
    definirEstado(escreverComoCliente(config, blocos, estado, texto));
    definirTexto('');
  };

  return (
    <Dialogo
      titulo="Testar como conversa"
      largura="larga"
      aoFechar={aoFechar}
      botoes={
        <>
          <Botao variante="secundario" onClick={() => recomecar(comeco)}>
            Recomeçar
          </Botao>
          <Botao variante="primario" onClick={aoFechar}>
            Fechar
          </Botao>
        </>
      }
    >
      <p className="conversa-de-teste__ajuda">
        Você é o cliente. Toque nas opções do menu ou escreva como ele escreveria. Nada é enviado: é
        o mapa como está na tela, mesmo sem salvar.
      </p>
      {config.outros_comecos.length > 0 && (
        <CampoSelecao
          rotulo="Começar por"
          tamanho="miudo"
          value={String(comeco)}
          onChange={(e) => recomecar(Number(e.target.value))}
        >
          <option value="0">{resumoDoComeco(config.gatilho, blocos)}</option>
          {config.outros_comecos.map((o, i) => (
            <option key={i} value={String(i + 1)}>
              {resumoDoComeco({ tipo: 'evento', evento: o.evento }, blocos)}
            </option>
          ))}
        </CampoSelecao>
      )}
      <ol className="conversa-de-teste" aria-label="A conversa de teste" aria-live="polite">
        {estado.falas.map((fala, i) => (
          <li key={i} className={`conversa-de-teste__fala conversa-de-teste__fala--${fala.quem}`}>
            {fala.texto && <span className="conversa-de-teste__texto">{fala.texto}</span>}
            {fala.opcoes && fala.opcoes.length > 0 && (
              <ul className="conversa-de-teste__opcoes" aria-label="Opções do menu">
                {fala.opcoes.map((opcao) => (
                  <li key={opcao.id}>
                    {i === indiceDoMenuAberto ? (
                      <button
                        type="button"
                        className="conversa-de-teste__opcao"
                        onClick={() => definirEstado(escolherOpcao(config, blocos, estado, opcao))}
                      >
                        {opcao.rotulo}
                      </button>
                    ) : (
                      <span className="conversa-de-teste__opcao conversa-de-teste__opcao--fechada">
                        {opcao.rotulo}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ol>

      {pergunta?.tipo === 'menu' && pergunta.semResposta && (
        <div className="conversa-de-teste__decisao">
          <Botao
            variante="fantasma"
            miudo
            onClick={() => definirEstado(seguirPor(config, blocos, estado, SEM_RESPOSTA))}
          >
            O cliente não respondeu
          </Botao>
        </div>
      )}
      {pergunta?.tipo === 'escolher' && (
        <div className="conversa-de-teste__decisao" role="group" aria-label={pergunta.texto}>
          <span>{pergunta.texto}</span>
          <div className="conversa-de-teste__saidas">
            {pergunta.saidas.map((s) => (
              <Botao
                key={s.saida}
                variante="secundario"
                miudo
                onClick={() => definirEstado(seguirPor(config, blocos, estado, s.saida))}
              >
                {s.rotulo}
              </Botao>
            ))}
          </div>
        </div>
      )}

      <form className="conversa-de-teste__escrever" onSubmit={escrever}>
        <Campo
          rotulo="Escrever como o cliente"
          value={texto}
          onChange={(e) => definirTexto(e.target.value)}
        />
        <Botao
          variante="secundario"
          type="submit"
          desabilitado={texto.trim() === ''}
          motivo="Escreva a mensagem do cliente."
        >
          Enviar
        </Botao>
      </form>
    </Dialogo>
  );
}
